import React, { useEffect, useMemo, useState } from "react";
import { ModalBody, Form, FormGroup, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import {
  addMinutes,
  differenceInDays,
  differenceInMinutes,
  format,
  isSameDay,
  parseISO,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  subMinutes,
} from "date-fns";
import { toast } from "react-toastify";
import { useApolloClient } from "@apollo/client";
import { useBookingsContext } from "../../Context/BookingsContext";
import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import { useBookingsMapContext } from "../Context/BookingsMapContext";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { useCalendarContext } from "../../../../lib/context/Calendar/CalendarContext";
import {
  useFormatReservationDate as formatReservationDate,
  forReservationDate,
  createFilterPassedTime,
  getAllDayTimeWindow,
} from "../../lib/datePickerHelper";
import { useCalendarRequests } from "../../../../api/grpc/calendarprovider/useCalendarRequests";
import { useRouter } from "../../../../lib/hooks/useRouter";
import { useCheckUserPermissions } from "../../../../lib/hooks/useCheckUserPermissions";
import { useAllReservationContext } from "../../../AllReservations/shared/context/AllReservationContext/AllReservationContext";
import { useWorkplacesClient } from "../../../../api/grpc/workplaces/useWorkplacesClient";
import { useReserveHelpers } from "../../lib/reserveHelpers";

import { capitalizeFirstLetter } from "../../../../lib/helpers/capitalizeFirstLetter";
import {
  getWorkplaceFragment,
  getWorkplaceFragmentName,
} from "../../../Workplaces/helpers/getWorkplaceFragment";
import {
  attachHoursToDate,
  dateToHoursAndMinutes,
} from "../../lib/dateInputConvert";
import { defineDatesPicker } from "../../lib/defineDatesPicker";
import { defineLocationPath } from "../../helpers/defineLocationPath";

import { Button } from "../../../shared/Button/Button";
import { Icon } from "../../../shared/Icon/Icon";
import { Input } from "../../../shared/Input/Input";
import { Attendee } from "../../../../api/grpc/booking/ggevent/ggevent";
import { CalendarCredentialsStatus } from "../../../../api/grpc/calendarprovider/calendarprovider";
import { MainResources } from "../../../../lib/types/main.types";
import { defaultAttendee } from "../../../../lib/helpers/defaultRoomEvent";
import { DaySelectorInput } from "../../../shared/Forms/DaySelectorInput/DaySelectorInput";
import { RoomBookingInputs } from "../../form/RoomBookingInputs/RoomBookingInputs";
import { Switch } from "../../../shared/Switch/Switch";
import { FindPeopleButton } from "../../form/FindPeopleButton/FindPeopleButton";
import { EndTimeInputs } from "../../form/EndTimeInputs/EndTimeInputs";
import { timePickerProps } from "../../helpers/timePickerProps";
import { SearchDesk } from "../../../../api/grpc/workplaces/workplaces";
import { BookingsModal } from "../../shared/BookingsModal/BookingsModal";
import {
  BOOKING_INTERVAL,
  ERROR_STRINGS,
  GENERAL_STRINGS,
  DESK_BOOKING_INTERVAL,
  INFO_STRINGS,
} from "../../../../lib/utils/constants";
import { UserPermissions } from "../../../../api/grpc/permissions/permissions";

interface Props {
  id: string;
  typeOfWorkplace: MainResources;
  bookingWindow?: number;
  refetch: () => void;
}

export const BookingsMapReserve = ({
  id,
  typeOfWorkplace,
  bookingWindow,
  refetch,
}: Props) => {
  const INTERVAL =
    typeOfWorkplace === "room" ? BOOKING_INTERVAL : DESK_BOOKING_INTERVAL;
  const { timeZone } = useTimeZoneContext();
  const { personalCalendar, refetchPersonalCalendar } = useCalendarContext();
  const { validateCalendarIntegrationCredentials } = useCalendarRequests();
  const { pathname } = useRouter();
  const [assignedDesk, setAssignedDesk] = useState<SearchDesk | undefined>(
    undefined
  );
  const {
    allDay,
    setAllDay,
    bookOnBehalfUser,
    setBookOnBehalfUser,
    notifyAssignee,
    setNotifyAssignee,
    day: daySelected,
    reserveLaterTime,
    dateAndTime,
    setReserveLaterTime,
  } = useBookingsMapContext();

  const { datePickOptions } = defineDatesPicker(timeZone);
  const { startDate, eventEndTime } = useAllReservationContext();

  const {
    subject,
    handleSubject,
    isOnlineMeeting,
    handleIsOnlineMeeting,
    makePrivate,
    handleMakePrivate,
    attendees,
    handleAttendees,
  } = useBookingsContext();

  const { getAssignedDesks } = useWorkplacesClient();

  const { checkUserPermission } = useCheckUserPermissions();

  const [startTime, setStartTime] = useState(
    toZonedTime(new Date(), timeZone)
  );
  const [endTime, setEndTime] = useState(
    toZonedTime(addMinutes(new Date(), INTERVAL), timeZone)
  );
  const [submitAttempt, setSubmitAttempt] = useState(false);

  const {
    handleReserveRoom,
    locationPath,
    handleReserveDesk,
    handleReserveMultiDayDesk,
    organizerName,
    isOpen,
    toggleModal,
  } = useReserveHelpers();

  const [day, setDay] = useState<string>(
    format(toZonedTime(new Date(), timeZone), "yyyy-MM-dd")
  );
  const [dayDates, setDayDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const isMultiDayDeskBooking =
    typeOfWorkplace === "desk" && dayDates.length > 1;

  // The main `day` prop on the confirmation modal already shows the earliest
  // date, so these are the rest of the selected dates for a multi-day desk
  // booking.
  const additionalReservationDays = isMultiDayDeskBooking
    ? [...dayDates]
        .sort()
        .slice(1)
        .map((selectedDay) =>
          forReservationDate(parseISO(selectedDay), timeZone)
        )
    : undefined;

  const exceedsBookingWindow =
    bookingWindow !== undefined &&
    (dayDates.length ? dayDates : [day]).some(
      (selectedDate) =>
        differenceInDays(
          startOfDay(parseISO(selectedDate)),
          startOfDay(toZonedTime(new Date(), timeZone))
        ) > bookingWindow
    );

  const isAllReservation = pathname.includes("all-reservations");

  const client = useApolloClient();
  const { user } = useAuthContext();

  const workplaceData = client.readFragment({
    id: `${capitalizeFirstLetter(typeOfWorkplace)}:${id}`,
    fragmentName: getWorkplaceFragmentName(
      capitalizeFirstLetter(typeOfWorkplace),
      true
    ),
    fragment: getWorkplaceFragment(
      capitalizeFirstLetter(typeOfWorkplace),
      true
    ),
  });

  useEffect(() => {
    setDayDates([]);

    if (allDay) {
      setAllDay(false);
    }

    if (!!subject.length) {
      handleSubject("");
    }

    if (bookOnBehalfUser) {
      setBookOnBehalfUser(undefined);
    }

    if (notifyAssignee) {
      setNotifyAssignee(false);
    }

    if (attendees.length > 0) {
      handleAttendees([]);
    }

    if (makePrivate) {
      handleMakePrivate();
    }

    if (!isAllReservation) {
      setDay(
        format(toZonedTime(new Date(daySelected), timeZone), "yyyy-MM-dd")
      );

      let newDate;

      if (differenceInMinutes(new Date(), new Date(dateAndTime)) >= 0) {
        newDate = new Date();
      } else {
        newDate = new Date(dateAndTime);
      }

      const defineDate = reserveLaterTime || newDate;

      setStartTime(toZonedTime(new Date(defineDate), timeZone));
      setEndTime(
        toZonedTime(addMinutes(new Date(defineDate), INTERVAL), timeZone)
      );
    }

    if (isAllReservation) {
      if (startDate) {
        setDay(
          format(toZonedTime(new Date(startDate), timeZone), "yyyy-MM-dd")
        );
      }

      if (eventEndTime) {
        setStartTime(toZonedTime(new Date(eventEndTime), timeZone));
        setEndTime(
          toZonedTime(addMinutes(new Date(eventEndTime), INTERVAL), timeZone)
        );
      }
    }
  }, []);

  const handleReserveWorkplace = async ({
    showWarning,
  }: {
    showWarning: boolean;
  }) => {
    setSubmitAttempt(true);

    if (
      !isSameDay(new Date(calculatedEndDate), new Date(calculatedStartDate)) &&
      !allDay
    ) {
      return toast.error(ERROR_STRINGS.endTimeNextDay);
    }

    if (!subject.length && typeOfWorkplace === "room") {
      return;
    }

    try {
      if (workplaceData.isBlocked) {
        return toast.error(
          `Reservations blocked for ${typeOfWorkplace} due to admin policy`
        );
      }
      setLoading(true);
      const eventIsForOtherUser = !!bookOnBehalfUser;

      if (showWarning) {
        const { response } = await getAssignedDesks({
          accountID: eventIsForOtherUser ? bookOnBehalfUser.accountID : "",
          customerID: eventIsForOtherUser ? bookOnBehalfUser.customerID : "",
        });

        if (response?.desks.length > 0) {
          setAssignedDesk(response.desks[0]);

          setLoading(false);

          return;
        }
      }

      const timeDifference = differenceInMinutes(
        toZonedTime(new Date(), timeZone),
        startTime
      );

      const validTimePassed = timeDifference <= 5 && timeDifference > 0;
      let zonedDay = fromZonedTime(day, timeZone);

      let start = attachHoursToDate(
        toZonedTime(zonedDay, timeZone).toISOString(),
        validTimePassed ? toZonedTime(new Date(), timeZone) : startTime,
        timeZone
      );

      if (differenceInMinutes(new Date(), new Date(start)) > 5) {
        setLoading(false);
        return toast.error("Invalid From time!");
      }

      if (typeOfWorkplace === "desk") {
        const deskReserveArgs = {
          accountId: eventIsForOtherUser
            ? bookOnBehalfUser.accountID
            : user?.claims.user_id || "",
          checkInReminderSend: workplaceData?.showCheckInTime,
          customerID: user?.customerid || "",
          deskId: id,
          organizer: eventIsForOtherUser
            ? {
                ...defaultAttendee,
                email: bookOnBehalfUser.email,
              }
            : undefined,
        };

        if (isMultiDayDeskBooking) {
          const multiDayTimeSlots = dayDates.map((selectedDay) => {
            const zonedSelectedDay = fromZonedTime(selectedDay, timeZone);

            return {
              startTime: attachHoursToDate(
                toZonedTime(zonedSelectedDay, timeZone).toISOString(),
                validTimePassed
                  ? toZonedTime(new Date(), timeZone)
                  : startTime,
                timeZone
              ),
              endTime: attachHoursToDate(
                toZonedTime(zonedSelectedDay, timeZone).toISOString(),
                endTime,
                timeZone,
                allDay
              ),
            };
          });

          await handleReserveMultiDayDesk({
            ...deskReserveArgs,
            dates: multiDayTimeSlots,
          });
        } else {
          await handleReserveDesk({
            ...deskReserveArgs,
            startTime: start,
            endTime: attachHoursToDate(
              toZonedTime(zonedDay, timeZone).toISOString(),
              endTime,
              timeZone,
              allDay
            ),
          });
        }

        setReserveLaterTime(undefined);
        setLoading(false);

        return;
      }

      let hasPersonalCalendar = personalCalendar.length > 0;
      let statusOfPersonalIntegrationToken;

      if (hasPersonalCalendar) {
        try {
          await refetchPersonalCalendar();

          const {
            response: { status },
          } = await validateCalendarIntegrationCredentials(
            personalCalendar[0].iD
          );

          statusOfPersonalIntegrationToken = status;
        } catch (error: any) {
          toast.error(error?.message);
          return;
        }
      }

      if (
        hasPersonalCalendar &&
        statusOfPersonalIntegrationToken ===
          CalendarCredentialsStatus.CalendarCredentialsStatusUnknown
      ) {
        return toast.info(
          "Couldn't define the status of the personal integration, please contact your administrator."
        );
      }

      const hasValidPersonalToken =
        statusOfPersonalIntegrationToken ===
        CalendarCredentialsStatus.CalendarCredentialsStatusValid;
      const isMode2Reservation = hasPersonalCalendar && hasValidPersonalToken;

      const organizerEmail = (): string => {
        if (eventIsForOtherUser) {
          return bookOnBehalfUser.email;
        }

        if (isMode2Reservation) {
          return user?.email || "";
        }

        return workplaceData?.resourceEmail;
      };

      const ateendeeEmail = (): string => {
        if (eventIsForOtherUser) {
          return bookOnBehalfUser.email;
        }

        if (isMode2Reservation) {
          return workplaceData?.resourceEmail;
        }

        return user?.email || "";
      };

      let attendee: Attendee[] = [
        {
          ...defaultAttendee,
          email: ateendeeEmail(),
        },
      ];

      attendees.map((item) => {
        return attendee.push({
          ...defaultAttendee,
          email: item,
        });
      });

      await handleReserveRoom({
        roomId: id,
        title: subject,
        attendees: attendee,
        onBehalfUserId: eventIsForOtherUser
          ? bookOnBehalfUser.accountID
          : undefined,
        calendarId:
          isMode2Reservation && !eventIsForOtherUser
            ? ""
            : workplaceData?.calendarId,
        calendarproviderId:
          isMode2Reservation && !eventIsForOtherUser
            ? personalCalendar[0].iD
            : workplaceData?.calendarProviderId,
        startTime: start,
        endTime: attachHoursToDate(
          toZonedTime(zonedDay, timeZone).toISOString(),
          endTime,
          timeZone,
          allDay
        ),
        organizerEmail: organizerEmail(),
        checkInReminderSend: !!workplaceData?.displaySettings?.checkinReminder,
        isOnlineMeeting: isOnlineMeeting,
        isPrivate: makePrivate,
      });

      setReserveLaterTime(undefined);

      setLoading(false);
    } catch (error: any) {
      toast.error(
        error.message ? error.message : ERROR_STRINGS.reservationFail
      );
    }
  };

  const filterPassedTime = createFilterPassedTime(day, timeZone);

  const handleDurationChange = (hour: number, minute: number) => {
    const durationInMinutes = hour * 60 + minute;
    const newEndTime = addMinutes(startTime, durationInMinutes);
    setEndTime(newEndTime);
  };

  const handleCloseConfirmModal = () => {
    toggleModal();

    refetch();
  };

  const definedUsername = useMemo(() => {
    if (bookOnBehalfUser) {
      return bookOnBehalfUser.displayName || bookOnBehalfUser.email;
    }

    if (typeOfWorkplace === "desk" || !!!personalCalendar.length) {
      return user?.firstName + " " + user?.lastName;
    }

    return organizerName || "";
  }, [personalCalendar, organizerName, bookOnBehalfUser]);

  const calculatedStartDate = useMemo(() => {
    const timeDifference = differenceInMinutes(
      toZonedTime(new Date(), timeZone),
      startTime
    );
    const validTimePassed = timeDifference <= 5 && timeDifference > 0;
    let zonedDay = fromZonedTime(day, timeZone);

    return attachHoursToDate(
      toZonedTime(zonedDay, timeZone).toISOString(),
      validTimePassed ? toZonedTime(new Date(), timeZone) : startTime,
      timeZone
    );
  }, [day, startTime, timeZone]);

  const calculatedEndDate = useMemo(() => {
    let zonedDay = fromZonedTime(day, timeZone);

    return attachHoursToDate(
      toZonedTime(zonedDay, timeZone).toISOString(),
      endTime,
      timeZone,
      allDay
    );
  }, [day, endTime, timeZone, allDay]);

  return (
    <>
      {assignedDesk && (
        <BookingsModal
          isOpen={true}
          toggle={() => setAssignedDesk(undefined)}
          title="Heads up!"
          type="warning"
          workspaceType="desk"
          day=""
          endTime=""
          startTime=""
          classNames="modal-footer-row modal-footer-row-reverse"
          locationPath={defineLocationPath(assignedDesk.locationPath)}
          userName={definedUsername + `${bookOnBehalfUser ? "" : " (you)"}`}
          workspaceName={assignedDesk?.name || ""}
          everyDay={true}
          buttonTitle="Yes"
          buttonCancelTitle="No"
          handleModalClose={() => setAssignedDesk(undefined)}
          buttonClick={() => handleReserveWorkplace({ showWarning: false })}
          disableButton={loading}
          loadSpinner={loading}
        />
      )}

      {/* confirmation modal */}
      {isOpen ? (
        <BookingsModal
          isOpen={isOpen}
          toggle={toggleModal}
          workspaceType={typeOfWorkplace}
          title={
            isMultiDayDeskBooking
              ? `${dayDates.length} reservations confirmed!`
              : "Reservation confirmed!"
          }
          icon="reservation-confimed"
          day={formatReservationDate(
            toZonedTime(new Date(calculatedStartDate), timeZone)
          )}
          additionalDays={additionalReservationDays}
          workspaceName={workplaceData.name}
          startTime={dateToHoursAndMinutes(
            new Date(calculatedStartDate),
            timeZone
          )}
          endTime={dateToHoursAndMinutes(new Date(calculatedEndDate), timeZone)}
          userName={definedUsername}
          subject={subject}
          locationPath={defineLocationPath(locationPath)}
          handleModalClose={handleCloseConfirmModal}
          type="confirm"
          allDay={allDay}
          showAttendees
        />
      ) : (
        <>
          {" "}
          <ModalBody className="w-90 p-0">
            <div className="text-center BookingsButtonsModal__info">
              {/* <span>When do you need your {workplaceData?.__typename} ?</span> */}
              <Icon
                icon={
                  workplaceData?.__typename === "Desk"
                    ? "desk_icon_18x18"
                    : "room_icon_18x18"
                }
              />

              <span>{workplaceData?.name}</span>
            </div>

            <Form className="SearchWorkplaceForm">
              {typeOfWorkplace === "room" && (
                <FormGroup className="SearchWorkplaceForm--text SearchWorkplaceForm__subject">
                  <Input
                    type="text"
                    placeholder="Subject new reservation..."
                    id="subject"
                    name="subject"
                    onChange={(e) => handleSubject(e.target.value)}
                    isTouched={submitAttempt && subject.length <= 0}
                    errorMessage={GENERAL_STRINGS.subjectRequired}
                    className={
                      submitAttempt && subject.length <= 0 ? "form-error" : ""
                    }
                  />
                </FormGroup>
              )}

              <FormGroup className="SearchWorkplaceForm--text mb-0">
                <label className="pb-2">From</label>
                <DaySelectorInput
                  customDateVariant={typeOfWorkplace}
                  value={
                    day
                      ? {
                          label: formatReservationDate(parseISO(day)),
                          value: day,
                          dates: dayDates,
                        }
                      : datePickOptions[0]
                  }
                  onChange={(values) => {
                    if (values === null) {
                      return;
                    }

                    setDay(values.value);
                    setDayDates(values.dates ?? []);

                    if (allDay === true) {
                      const { start, end } = getAllDayTimeWindow(
                        values.value,
                        timeZone
                      );

                      setStartTime(start);
                      setEndTime(end);
                    }

                    return;
                  }}
                />
              </FormGroup>

              <FormGroup className="SearchWorkplaceForm--text">
                <DatePicker
                  {...timePickerProps}
                  selected={startTime}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      return;
                    }

                    setStartTime(date);

                    if (!allDay) {
                      setEndTime(addMinutes(new Date(date), INTERVAL));
                    }

                    return;
                  }}
                  className="pb-1 SearchWorkplaceForm__timePicker"
                  filterTime={filterPassedTime}
                />
              </FormGroup>

              <FormGroup className="SearchWorkplaceForm--text flex-a-center">
                <EndTimeInputs
                  handleDurationChange={handleDurationChange}
                  startTime={startTime}
                  endTime={endTime}
                  filterPassedTime={filterPassedTime}
                  is24Hour={
                    allDay === true &&
                    differenceInDays(
                      startOfDay(parseISO(day)),
                      startOfDay(toZonedTime(new Date(), timeZone))
                    ) > 0
                  }
                  onChange={(date: Date | null) => {
                    if (!date) {
                      return;
                    }

                    setEndTime(date);

                    if (date <= startTime && !allDay) {
                      setStartTime(subMinutes(new Date(date), INTERVAL));
                    }

                    return;
                  }}
                />
              </FormGroup>

              <div className="flex-a-center SearchWorkplaceForm__switch justify-content-center mb-3">
                <Switch
                  name="allDay"
                  title="Allday"
                  value={allDay}
                  onChange={() => {
                    const definedTimeEndTime =
                      allDay === false
                        ? setHours(
                            setMinutes(
                              setSeconds(
                                toZonedTime(new Date(), timeZone),
                                0
                              ),
                              0
                            ),
                            24
                          )
                        : toZonedTime(
                            addMinutes(new Date(), BOOKING_INTERVAL),
                            timeZone
                          );

                    const definedTimeStartTime =
                      allDay === false &&
                      differenceInDays(
                        startOfDay(parseISO(day)),
                        startOfDay(toZonedTime(new Date(), timeZone))
                      ) > 0
                        ? setHours(
                            setMinutes(
                              setSeconds(
                                toZonedTime(new Date(), timeZone),
                                0
                              ),
                              0
                            ),
                            24
                          )
                        : toZonedTime(new Date(), timeZone);

                    setEndTime(definedTimeEndTime);
                    setStartTime(definedTimeStartTime);

                    setAllDay((prev) => !prev);
                  }}
                />
              </div>

              <FindPeopleButton
                type={typeOfWorkplace}
                hideFindPeople={typeOfWorkplace === "desk"}
                showBookOnBehalf={
                  !checkUserPermission(UserPermissions.RSRVNS_MEMBER_STYLE_VIEW)
                }
              />

              {typeOfWorkplace === "room" && (
                <RoomBookingInputs
                  makePrivate={makePrivate}
                  onChangePrivate={() => handleMakePrivate()}
                  isOnlineMeeting={isOnlineMeeting}
                  onChangeOnlineMeeting={() => handleIsOnlineMeeting()}
                />
              )}
            </Form>
          </ModalBody>
          <ModalFooter className="justify-content-center w-90">
            <Button
              title="Reserve"
              color="business"
              size="medium"
              disabled={loading || exceedsBookingWindow}
              tooltip={
                exceedsBookingWindow
                  ? INFO_STRINGS.outsideBookingWindow
                  : undefined
              }
              loadSpinner={loading}
              className="max-w-none w-100 m-0 BookingsButtonsModal__reserveButton"
              onClick={() =>
                handleReserveWorkplace({
                  showWarning:
                    typeOfWorkplace === "desk" &&
                    (checkUserPermission(
                      UserPermissions.RSRVNS_MEMBER_STYLE_VIEW
                    ) ||
                      !isAllReservation),
                })
              }
            />
          </ModalFooter>
        </>
      )}
    </>
  );
};
